import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RouterIcon from "@mui/icons-material/Router";
import { API_URL } from "./config";

export default function CaptivePortalConfigPage() {
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";

  const pageWrapSx = { p: 2, backgroundColor: BG };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
    p: 2,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const buttonSx = {
    borderRadius: 2,
    px: 2.5,
    py: 1,
    fontWeight: 800,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    "&:hover": {
      background: `linear-gradient(90deg, ${THEME_ORANGE_2} 0%, ${THEME_ORANGE} 100%)`,
    },
  };

  const [form, setForm] = useState({
    ProviderCode: "UNIFI_LEGACY",
    ControllerUrl: "",
    SiteCode: "default",
    SiteID: "",
    ApiKey: "",
    Username: "",
    Password: "",
    PortalSuccessUrl: "https://www.google.com",
  });

  const [msg, setMsg] = useState("");
  const [severity, setSeverity] = useState("success");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionResult, setConnectionResult] = useState(null);

  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  const isOfficial = form.ProviderCode === "UNIFI_OFFICIAL";
  const isLegacy = form.ProviderCode === "UNIFI_LEGACY";

  const providerHelp = useMemo(() => {
    if (isOfficial) {
      return {
        title: "UniFi Cloud API",
        text: "Use this when the campus uses UniFi Site Manager / Cloud API. Enter API Key and Site ID. Username and password are not needed.",
        urlHint: "https://api.ui.com",
      };
    }

    if (isLegacy) {
      return {
        title: "UniFi Local Controller / UDM",
        text: "Use this when the campus has a local UniFi Controller, Cloud Key, or UDM. Enter controller URL, site code, username, and password.",
        urlHint: "https://192.168.1.1",
      };
    }

    return {
      title: "Open Source / Custom Provider",
      text: "Use this later for OpenNDS, pfSense, OpenWrt, or a custom captive portal gateway.",
      urlHint: "https://gateway.yourcampus.local",
    };
  }, [isOfficial, isLegacy]);

  const update = (name, value) => {
    setForm((p) => {
      const next = { ...p, [name]: value };

      if (name === "ProviderCode") {
        if (value === "UNIFI_OFFICIAL") {
          next.ControllerUrl = "https://api.ui.com";
          next.SiteCode = "";
          next.Username = "";
          next.Password = "";
        }

        if (value === "UNIFI_LEGACY") {
          next.ControllerUrl = "";
          next.SiteCode = "default";
          next.SiteID = "";
          next.ApiKey = "";
        }
      }

      return next;
    });
  };

  const validate = () => {
    if (!form.ProviderCode) return "Provider is required.";
    if (!form.ControllerUrl) return "Controller URL is required.";

    if (isOfficial) {
      if (!form.SiteID) return "Site ID is required for UniFi Official API.";
      if (!form.ApiKey) return "API Key is required for UniFi Official API.";
    }

    if (isLegacy) {
      if (!form.SiteCode) return "Site Code is required for UniFi Legacy / UniFi OS.";
      if (!form.Username) return "Username is required for UniFi Legacy / UniFi OS.";
      if (!form.Password) return "Password is required for UniFi Legacy / UniFi OS.";
    }

    return "";
  };

  const save = async () => {
    setMsg("");

    const validationError = validate();
    if (validationError) {
      setSeverity("error");
      setMsg(validationError);
      return;
    }

    setSaving(true);

    try {
      await axios.post(`${API_URL}/api/captive-portal/config`, form, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setSeverity("success");
      setMsg("Captive portal configuration saved.");
    } catch (err) {
      setSeverity("error");
      setMsg(err.response?.data?.error || "Failed to save configuration.");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
  
    try {
      const res = await axios.post(
        `${API_URL}/api/captive-portal/test-connection`,
        {
          ControllerUrl: form.ControllerUrl,
          ApiKey: form.ApiKey,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
  
      setConnectionResult({
        success: true,
        sites: res.data.sites || [],
      });
    } catch (err) {
      setConnectionResult({
        success: false,
        error:
          err.response?.data?.error ||
          "Unable to connect to UniFi",
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Captive Portal Setup
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Configure UniFLOO WiFi login for campus internet access
            </Typography>
          </Box>

          <Chip
            icon={<RouterIcon sx={{ color: "white !important" }} />}
            label={form.ProviderCode}
            sx={{
              color: "white",
              fontWeight: 800,
              border: "1px solid rgba(255,255,255,0.45)",
              backgroundColor: "rgba(255,255,255,0.12)",
            }}
          />
        </Box>

        <Box sx={bodySx}>
          {msg && (
            <Alert
              severity={severity}
              onClose={() => setMsg("")}
              sx={{
                mb: 2,
                borderRadius: 2,
                border:
                  severity === "success"
                    ? "1px solid rgba(76,175,80,0.25)"
                    : "1px solid rgba(244,67,54,0.25)",
              }}
            >
              {msg}
            </Alert>
          )}

          <Stack spacing={2}>
            <Paper elevation={0} sx={surfaceSx}>
              <Stack spacing={0.5}>
                <Typography fontWeight={900}>{providerHelp.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {providerHelp.text}
                </Typography>
              </Stack>
            </Paper>

            <Paper elevation={0} sx={surfaceSx}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    label="Provider"
                    value={form.ProviderCode}
                    onChange={(e) => update("ProviderCode", e.target.value)}
                    fullWidth
                    size="small"
                    sx={inputSx}
                  >
                    <MenuItem value="UNIFI_LEGACY">UniFi Local Controller / UDM</MenuItem>
                    <MenuItem value="UNIFI_OFFICIAL">UniFi Cloud API</MenuItem>
                    <MenuItem value="OPENNDS">OpenNDS</MenuItem>
                    <MenuItem value="PFSENSE">pfSense</MenuItem>
                    <MenuItem value="CUSTOM">Custom</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={8}>
                  <TextField
                    label="Controller URL"
                    value={form.ControllerUrl}
                    onChange={(e) => update("ControllerUrl", e.target.value)}
                    placeholder={providerHelp.urlHint}
                    fullWidth
                    size="small"
                    sx={inputSx}
                    helperText={
                      isOfficial
                        ? "For UniFi Cloud API, usually use https://api.ui.com"
                        : "For local UniFi, use UDM/Controller URL like https://192.168.1.1"
                    }
                  />
                </Grid>

                {isLegacy && (
                  <>
                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Site Code"
                        value={form.SiteCode}
                        onChange={(e) => update("SiteCode", e.target.value)}
                        placeholder="default"
                        fullWidth
                        size="small"
                        sx={inputSx}
                        helperText="Usually default"
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Username"
                        value={form.Username}
                        onChange={(e) => update("Username", e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        label="Password"
                        type="password"
                        value={form.Password}
                        onChange={(e) => update("Password", e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                  </>
                )}

                {isOfficial && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Site ID"
                        value={form.SiteID}
                        onChange={(e) => update("SiteID", e.target.value)}
                        placeholder="0a1b2c3d-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="API Key"
                        value={form.ApiKey}
                        onChange={(e) => update("ApiKey", e.target.value)}
                        placeholder="uapi_xxxxxxxxxxxxxxxxx"
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                  </>
                )}

                {!isOfficial && !isLegacy && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Site Code / Gateway ID"
                        value={form.SiteCode}
                        onChange={(e) => update("SiteCode", e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        label="API Key / Secret"
                        value={form.ApiKey}
                        onChange={(e) => update("ApiKey", e.target.value)}
                        fullWidth
                        size="small"
                        sx={inputSx}
                      />
                    </Grid>
                  </>
                )}

                <Grid item xs={12} md={4}>
                  <TextField
                    label="Success Redirect URL"
                    value={form.PortalSuccessUrl}
                    onChange={(e) => update("PortalSuccessUrl", e.target.value)}
                    placeholder="https://www.google.com"
                    fullWidth
                    size="small"
                    sx={inputSx}
                    helperText="After WiFi access is granted, user will be redirected here."
                  />
                </Grid>

                <Grid item xs={12} md={8}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    sx={{ width: "100%" }}
                  >
                    <Button
                      variant="outlined"
                      onClick={testConnection}
                      disabled={testing || saving}
                      fullWidth
                      sx={{ height: 40, borderRadius: 2, fontWeight: 800 }}
                    >
                      {testing ? "Testing..." : "Test Connection"}
                    </Button>
                
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={save}
                      disabled={saving || testing}
                      fullWidth
                      sx={{ ...buttonSx, height: 40 }}
                    >
                      {saving ? "Saving..." : "Save Configuration"}
                    </Button>
                  </Stack>
                
                  {connectionResult?.success && (
                    <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                      Connected successfully to UniFi. Found{" "}
                      {connectionResult.sites?.length || 0} site(s).
                    </Alert>
                  )}
                
                  {connectionResult?.success === false && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {connectionResult.error || "Unable to connect to UniFi."}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Paper>

            <Paper elevation={0} sx={surfaceSx}>
              <Typography fontWeight={900} gutterBottom>
                UniFi External Portal URL
              </Typography>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Add this URL in UniFi Hotspot / External Portal settings:
              </Typography>

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: "rgba(25,118,210,0.06)",
                  border: `1px solid ${BORDER}`,
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                }}
              >
                {`${API_URL}/api/captive-portal/portal?campusCode=YOUR_CAMPUS_CODE`}
              </Box>

              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                Example: {`${API_URL}/api/captive-portal/portal?campusCode=KIET`}
              </Typography>
            </Paper>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}